import React, { memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Icon, Spinner, Tooltip } from "@chakra-ui/react";
import { FaCheck, FaSpellCheck, FaExclamationTriangle } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import * as ImportPostStatus from "./ImportPostStatus.constants";
import SimplePost from "../SimplePost/SimplePost";
import SocialAccountPropType from "../../../_types/SocialAccount";
import PostsBulkImportItemMenu from "./PostsBulkImportItemMenu";

const PostsBulkImportItem = ({
  accounts,
  data,
  isDisabled,
  hasAutomaticTime,
  onDelete,
  onEdit,
  onClick,
}) => {
  const { t } = useTranslation(["planner"]);

  const handleDelete = useCallback(() => {
    onDelete?.(data);
  }, [onDelete, data]);

  const handleEdit = useCallback(() => {
    onEdit?.(data);
  }, [onEdit, data]);

  const handleClick = useCallback(() => {
    onClick?.(data);
  }, [onClick, data]);

  const statusIcon = useMemo(() => {
    let tooltipLabel = null;
    let icon = null;

    if (data.status === ImportPostStatus.VALIDATION_ERROR) {
      tooltipLabel =
        data.errors?.map((error, index) => (
          <Box key={`error-${index}`}>{error.message}</Box>
        )) || null;
      icon = <Icon as={FaSpellCheck} color="brand.negative" />;
    } else if (data.status === ImportPostStatus.IMPORT_ERROR) {
      tooltipLabel =
        data.errors?.map((error, index) => (
          <Box key={`error-${index}`}>{error.message}</Box>
        )) || null;
      icon = <Icon as={FaExclamationTriangle} color="brand.negative" />;
    } else if (data.status === ImportPostStatus.READY_TO_IMPORT) {
      tooltipLabel = t("posts_bulk_import.status.ready_to_import");
      icon = <Icon as={FaSpellCheck} color="gray.100" />;
    } else if (data.status === ImportPostStatus.IMPORTING) {
      tooltipLabel = t("posts_bulk_import.status.importing");
      icon = <Spinner size="sm" color="brand.primary" />;
    } else if (data.status === ImportPostStatus.IMPORT_SUCCESS) {
      tooltipLabel = t("posts_bulk_import.status.import_success");
      icon = <Icon as={FaCheck} color="brand.positive" />;
    }

    return (
      <Tooltip
        placement="top"
        label={tooltipLabel}
        hasArrow
        isDisabled={!tooltipLabel}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="4"
          fontSize="sm"
        >
          {icon}
        </Box>
      </Tooltip>
    );
  }, [data]);

  return (
    <Box>
      <SimplePost
        accounts={accounts}
        additional={{
          status: data.status,
        }}
        post={data.post}
        layout="horizontal"
        type="custom"
        customStatusIcon={statusIcon}
        customMenu={(menuProps) => (
          <PostsBulkImportItemMenu
            {...menuProps}
            buttonProps={{
              ...menuProps?.buttonProps,
              isDisabled,
            }}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        )}
        customEmptyDateTimeText={
          hasAutomaticTime
            ? t("posts_bulk_import.post.automatic_publication_time")
            : null
        }
        isPast={!!data.errors?.length}
        onClick={handleClick}
      />
    </Box>
  );
};

PostsBulkImportItem.propTypes = {
  accounts: PropTypes.arrayOf(SocialAccountPropType).isRequired,
  data: PropTypes.shape({}).isRequired,
  isDisabled: PropTypes.bool,
  hasAutomaticTime: PropTypes.bool,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onClick: PropTypes.func,
};

PostsBulkImportItem.defaultProps = {
  isDisabled: undefined,
  hasAutomaticTime: undefined,
  onDelete: undefined,
  onEdit: undefined,
  onClick: undefined,
};

export default memo(PostsBulkImportItem);
